import { useCallback, useMemo, useRef, useState } from 'react';

// Selection changes AG Grid raises on its own (rowData replaced by search/filter/
// paging) or that we raise while restoring — they must not drop hidden picks.
const PROGRAMMATIC_SOURCES = new Set(['rowDataChanged', 'api', 'gridInitializing', 'selectableChanged']);

export const isUserSelectionSource = (source) => !PROGRAMMATIC_SOURCES.has(source);

// Applies the tick state of the rows currently in the grid; rows the filter hides stay selected.
export const mergeVisibleSelection = (prev, visibleNodes) =>
  visibleNodes.reduce((acc, { id, data, selected }) => {
    if (selected) return { ...acc, [id]: data };
    return id in acc ? removeSelected(acc, (_data, key) => key === id) : acc;
  }, prev);

// Keeps stored rows current (status, released_at…) when they come back from the server.
export const refreshSelectedData = (prev, visibleNodes) =>
  visibleNodes.reduce(
    (acc, { id, data }) => (id in acc && acc[id] !== data ? { ...acc, [id]: data } : acc),
    prev
  );

export function removeSelected(prev, predicate) {
  return Object.fromEntries(Object.entries(prev).filter(([key, data]) => !predicate(data, key)));
}

const selectableNodes = (api) => {
  const nodes = [];
  api.forEachNode((node) => { if (node.data && node.selectable) nodes.push(node); });
  return nodes;
};

/**
 * Row selection for an AG Grid whose rowData is refetched on every filter change.
 * Selections are keyed by row id and survive search / filter / paging until cleared.
 * Wire: onSelectionChanged, onRowDataUpdated + onFirstDataRendered → restoreSelection.
 */
const usePersistentRowSelection = (gridRef) => {
  const [selectedMap, setSelectedMap] = useState({});
  // Read by grid callbacks that can fire before this component re-renders.
  const selectedMapRef = useRef(selectedMap);

  const updateSelection = useCallback((updater) => {
    setSelectedMap((prev) => {
      const next = updater(prev);
      selectedMapRef.current = next;
      return next;
    });
  }, []);

  const onSelectionChanged = useCallback((event) => {
    if (!isUserSelectionSource(event.source)) return;
    const visible = selectableNodes(event.api).map((node) => ({ id: node.id, data: node.data, selected: node.isSelected() }));
    updateSelection((prev) => mergeVisibleSelection(prev, visible));
  }, [updateSelection]);

  const restoreSelection = useCallback((event) => {
    const current = selectedMapRef.current;
    const kept = selectableNodes(event.api).filter((node) => node.id in current);
    if (!kept.length) return;
    const toTick = kept.filter((node) => !node.isSelected());
    if (toTick.length) event.api.setNodesSelected({ nodes: toTick, newValue: true, source: 'api' });
    updateSelection((prev) => refreshSelectedData(prev, kept.map((node) => ({ id: node.id, data: node.data }))));
  }, [updateSelection]);

  const clearSelection = useCallback(() => {
    updateSelection(() => ({}));
    gridRef.current?.api?.deselectAll('api');
  }, [gridRef, updateSelection]);

  const removeWhere = useCallback((predicate) => {
    updateSelection((prev) => removeSelected(prev, predicate));
  }, [updateSelection]);

  const selectedRows = useMemo(() => Object.values(selectedMap), [selectedMap]);

  return { selectedRows, onSelectionChanged, restoreSelection, clearSelection, removeWhere };
};

export default usePersistentRowSelection;
